import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Play, 
  Loader2, 
  CheckCircle, 
  RefreshCw, 
  Settings, 
  Users, 
  Zap,
  MoreHorizontal 
} from 'lucide-react';

interface BulkActionsProps {
  selectedCount: number;
  isLoading: boolean;
  isBulkRerunning: boolean;
  isBulkUpdatingScraper: boolean;
  isBulkVerifying: boolean;
  onBulkRerun: () => void;
  onBulkUpdateScraper: (scraper: string) => void;
  onBulkVerify: () => void;
  onVerifyAll: () => void;
  onRefresh: () => void;
}

export const BulkActions: React.FC<BulkActionsProps> = ({
  selectedCount,
  isLoading,
  isBulkRerunning,
  isBulkUpdatingScraper,
  isBulkVerifying,
  onBulkRerun,
  onBulkUpdateScraper,
  onBulkVerify,
  onVerifyAll,
  onRefresh,
}) => {
  const [selectedScraper, setSelectedScraper] = useState<string>('default');
  
  if (selectedCount > 0) {
    return (
      <Card className="p-3 bg-blue-50 border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              {selectedCount} selected
            </span>
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              Bulk Actions
            </Badge>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Rerun Action */}
            <Button
              variant="destructive"
              size="sm"
              onClick={onBulkRerun}
              disabled={isBulkRerunning || isBulkUpdatingScraper || isBulkVerifying}
              className="h-8"
            >
              {isBulkRerunning ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Rerunning...
                </>
              ) : (
                <>
                  <Play className="h-3 w-3 mr-1" />
                  Rerun
                </>
              )}
            </Button>
            
            {/* Scraper Update */}
            <div className="flex items-center gap-1">
              <Select value={selectedScraper} onValueChange={setSelectedScraper}>
                <SelectTrigger className="w-28 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="scrapingBee">ScrapingBee</SelectItem>
                  <SelectItem value="firecrawl">Firecrawl</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkUpdateScraper(selectedScraper)}
                disabled={isBulkUpdatingScraper || isBulkRerunning || isBulkVerifying}
                className="h-8"
              >
                {isBulkUpdatingScraper ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Settings className="h-3 w-3 mr-1" />
                    Set
                  </>
                )}
              </Button>
            </div>
            
            {/* Verify Action */}
            <Button
              variant="outline"
              size="sm"
              onClick={onBulkVerify}
              disabled={isBulkVerifying || isBulkRerunning || isBulkUpdatingScraper}
              className="h-8"
            >
              {isBulkVerifying ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Verify
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    );
  }
  
  // Global actions when nothing is selected
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={onVerifyAll}
        className="h-8"
      >
        <Zap className="h-3 w-3 mr-1" />
        Verify All
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onRefresh}
        disabled={isLoading}
        className="h-8"
      >
        {isLoading ? (
          <>
            <RefreshCw className="h-3 w-3 animate-spin mr-1" />
            Loading...
          </>
        ) : (
          <>
            <RefreshCw className="h-3 w-3 mr-1" />
            Refresh
          </>
        )}
      </Button>
    </div>
  );
};
